import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Image,
} from 'react-native';

interface PackageInfo {
  type: string;
  vip?: {
    durationDays: number;
  };
  chips?: number;
  id?: string;
  name?: string;
}

interface PaymentStatusModalProps {
  visible: boolean;
  status: 'success' | 'cancelled' | 'error';
  packageInfo?: PackageInfo;
  onClose: () => void;
  autoDismiss?: boolean;
  autoDismissDelay?: number;
}

export const PaymentStatusModal: React.FC<PaymentStatusModalProps> = ({
  visible,
  status,
  packageInfo,
  onClose,
  autoDismiss = true,
  autoDismissDelay = 5000,
}) => {
  const [scaleAnim] = useState(new Animated.Value(0));

  useEffect(() => {
    if (visible) {
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }).start();

      if (autoDismiss && status === 'success') {
        const timer = setTimeout(() => {
          handleClose();
        }, autoDismissDelay);

        return () => clearTimeout(timer);
      }
    }
  }, [visible, autoDismiss, autoDismissDelay, status]);

  const handleClose = () => {
    Animated.spring(scaleAnim, {
      toValue: 0,
      tension: 50,
      friction: 7,
      useNativeDriver: true,
    }).start(() => {
      onClose();
    });
  };

  const isSuccess = status === 'success';
  const isCancelled = status === 'cancelled';
  const statusTextColor = isSuccess ? '#10b981' : isCancelled ? '#ffa500' : '#ef4444';

  const getStatusTitle = () => {
    switch (status) {
      case 'success':
        return 'Payment Successful!';
      case 'cancelled':
        return 'Payment Cancelled';
      case 'error':
        return 'Payment Failed';
      default:
        return 'Payment Status';
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'success':
        return 'Your purchase has been completed successfully.';
      case 'cancelled':
        return 'The payment was not completed. Please try again.';
      case 'error':
        return 'There was an error processing your payment. Please try again.';
      default:
        return '';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return '✓';
      case 'cancelled':
        return '⚠';
      case 'error':
        return '✕';
      default:
        return '?';
    }
  };

  const formatPackageInfo = () => {
    if (!packageInfo) return null;

    if (packageInfo.type === 'VIP_MEMBERSHIP' && packageInfo.vip) {
      return {
        title: packageInfo.name || 'VIP Membership',
        details: `${packageInfo.vip.durationDays} Days`,
        icon: '👑',
      };
    } else if (packageInfo.type === 'CHIPS') {
      console.log("packageInfo", packageInfo);
      const chips = packageInfo.chips || packageInfo.chipAmount || 0;
      return {
        title: packageInfo.name || 'Chips Package',
        details: `${chips.toLocaleString()} Chips`,
        icon: '🎰',
      };
    } else if (packageInfo.type === 'Casino Points' || packageInfo.type === 'POINTS') {
      return {
        title: 'Casino Points',
        details: 'Added to your wallet',
        icon: '💰',
      };
    }
    return null;
  };

  const packageDetails = formatPackageInfo();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <TouchableOpacity
          style={styles.overlayTouchable}
          activeOpacity={1}
          onPress={handleClose}
        />
        <Animated.View
          style={[
            styles.modalContainer,
            {
              transform: [{ scale: scaleAnim }],
            },
          ]}
        >
          {/* Close X Button */}
          <TouchableOpacity
            style={styles.closeXButton}
            onPress={handleClose}
            activeOpacity={0.7}
          >
            <Text style={styles.closeXText}>✕</Text>
          </TouchableOpacity>

          {/* Status Icon with colored border */}
          <View style={[styles.iconContainer, { borderColor: statusTextColor }]}>
            <Text style={[styles.iconText, { color: statusTextColor }]}>
              {getStatusIcon()}
            </Text>
          </View>

          {/* Title - Only status text is colored */}
          <Text style={[styles.title, { color: statusTextColor }]}>
            {getStatusTitle()}
          </Text>

          {/* Message - White text */}
          <Text style={styles.message}>
            {getStatusMessage()}
          </Text>

          {/* Package Information */}
          {isSuccess && packageDetails && (
            <View style={styles.packageContainer}>
              <Text style={styles.packageIcon}>{packageDetails.icon}</Text>
              <View style={styles.packageTextContainer}>
                <Text style={styles.packageTitle}>
                  {packageDetails.title}
                </Text>
                <Text style={styles.packageDetails}>
                  {packageDetails.details}
                </Text>
              </View>
            </View>
          )}

          {/* Close Button */}
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            activeOpacity={0.8}
          >
            <Text style={styles.closeButtonText}>
              {isSuccess ? 'Great!' : 'Close'}
            </Text>
          </TouchableOpacity>

        
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlayTouchable: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContainer: {
    width: '85%',
    maxWidth: 450,
    backgroundColor: '#112345', 
    borderRadius: 16,
    borderWidth: 2,
    borderColor: '#00f0ff', 
    padding: 30,
    alignItems: 'center',
    shadowColor: '#00f0ff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 15,
    elevation: 10,
  },
  closeXButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 240, 255, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(0, 240, 255, 0.3)',
    zIndex: 10,
  },
  closeXText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#00f0ff', 
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
    backgroundColor: 'rgba(0, 240, 255, 0.1)', 
    borderWidth: 3,
  },
  iconText: {
    fontSize: 48,
    fontWeight: 'bold',

  },
  title: {
    fontSize: 26,
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,

  },
  message: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    color: '#ffffff', 
    opacity: 0.9,
    lineHeight: 22,
  },
  packageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 240, 255, 0.08)', 
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(0, 240, 255, 0.3)',
    padding: 16,
    marginBottom: 24,
    width: '100%',
  },
  packageIcon: {
    fontSize: 36,
    marginRight: 14,
  },
  packageTextContainer: {
    flex: 1,
  },
  packageTitle: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 4,
    color: '#ffd700', 
  },
  packageDetails: {
    fontSize: 15,
    color: '#ffffff', 
    opacity: 0.8,
  },
  closeButton: {
    backgroundColor: '#00f0ff',
    paddingVertical: 14,
    paddingHorizontal: 40,
    borderRadius: 25,
    minWidth: 140,
    shadowColor: '#00f0ff',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 5,
  },
  closeButtonText: {
    color: '#000000',
    fontSize: 17,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  autoDismissText: {
    marginTop: 12,
    fontSize: 12,
    color: '#00f0ff', 
    opacity: 0.7,
    textAlign: 'center',
  },
});